/**
 * SavedFiltersControl.js
 *
 * LGX-653: dropdown for a pageKey's saved filters -- lists the viewer's own filters plus any
 * ORG-visibility filters shared by someone else in the same org tree, lets the viewer apply one,
 * save the current filter tree (with a Private/Org visibility choice), and delete their own.
 */
import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Save, Loader2, Trash2, Lock, Users } from "lucide-react";
import { savedFiltersApi } from "./savedFiltersApi";
import { buildFilterNode, makeRuleId } from "./filterTree";
import notify from "../../components/ACS-Dashboard/ACS-Blocks/Toast";

// Inverse of buildFilterNode/nodeToFilterNode: a saved filter is persisted server-side as one
// backend FilterNode tree (field is the BACKEND field name), not the UI's rules[] tree (field is
// the ruleFields `key`) -- applying a saved filter back into the editable modal means walking it
// back into that shape via each field's backendField.
const filterNodeToRuleNode = (ruleFields, filterNode) => {
  if (!filterNode) return null;
  if (filterNode.operator && filterNode.filters?.length) {
    const children = filterNode.filters
      .map((f) => filterNodeToRuleNode(ruleFields, f))
      .filter(Boolean);
    if (!children.length) return null;
    return { id: makeRuleId(), matchType: filterNode.operator, children };
  }
  const field = ruleFields.find((f) => f.backendField === filterNode.field);
  if (!field) return null;
  const value = filterNode.op === "IN" && typeof filterNode.value === "string"
    ? filterNode.value.split(",")
    : filterNode.value;
  return { id: makeRuleId(), field: field.key, op: filterNode.op, value };
};

/** Backend FilterNode -> { rules, matchType } the AdvancedFilterModal/toolbar can render and edit. */
const filterNodeToFilters = (ruleFields, filterNode) => {
  if (!filterNode) return { rules: [], matchType: "AND" };
  if (filterNode.operator && filterNode.filters?.length) {
    const rules = filterNode.filters
      .map((f) => filterNodeToRuleNode(ruleFields, f))
      .filter(Boolean);
    return { rules, matchType: filterNode.operator };
  }
  const node = filterNodeToRuleNode(ruleFields, filterNode);
  return { rules: node ? [node] : [], matchType: "AND" };
};

/**
 * @param {string} pageKey        - e.g. "orders"
 * @param {Array}  ruleFields     - this grid's field catalog (see filterTree.js)
 * @param {object} currentFilters - { rules, matchType } currently applied, used when saving
 * @param {func}   onApply        - (filters) => void, called when a saved filter is chosen
 */
export const SavedFiltersControl = ({ pageKey, ruleFields, currentFilters, onApply }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filters, setFilters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [name, setName] = useState("");
  const [visibility, setVisibility] = useState("PRIVATE");
  const [busy, setBusy] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setShowSaveForm(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const load = () => {
    setLoading(true);
    savedFiltersApi
      .list(pageKey)
      .then((res) => setFilters(res.data || []))
      .catch(() => setFilters([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen]);

  const hasActiveFilter = (currentFilters?.rules || []).length > 0;

  const handleSave = async () => {
    if (!name.trim()) return;
    const filterNode = buildFilterNode(ruleFields, currentFilters);
    if (!filterNode) return;
    setBusy(true);
    try {
      await savedFiltersApi.create({ pageKey, name: name.trim(), visibility, filter: filterNode });
      notify.success({ title: "Filter saved" });
      setName("");
      setVisibility("PRIVATE");
      setShowSaveForm(false);
      load();
    } catch (err) {
      notify.error({ title: "Could not save filter", message: err?.response?.data?.message || err.message });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await savedFiltersApi.remove(id);
      setFilters((prev) => prev.filter((f) => f.id !== id));
    } catch (err) {
      notify.error({ title: "Could not delete filter", message: err?.response?.data?.message || err.message });
    }
  };

  const handleApply = (filter) => {
    onApply(filterNodeToFilters(ruleFields, filter.filter));
    setIsOpen(false);
  };

  const mine = filters.filter((f) => f.mine);
  const shared = filters.filter((f) => !f.mine);

  return (
    <div className="relative inline-block" ref={containerRef}>
      <button
        onClick={() => setIsOpen((p) => !p)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface text-gray-700 dark:text-acs-dark-text hover:border-blue-400 dark:hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
      >
        Saved Filters
        <ChevronDown size={14} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-80 bg-white dark:bg-acs-dark-surface border border-gray-200 dark:border-acs-dark-border rounded-lg shadow-xl z-50 max-h-[24rem] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-4 gap-2 text-gray-500 text-xs">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              {mine.length === 0 && shared.length === 0 && (
                <p className="px-3 py-3 text-xs text-gray-400 dark:text-gray-500">No saved filters yet.</p>
              )}

              {mine.length > 0 && (
                <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                  My filters
                </div>
              )}
              {mine.map((f) => (
                <FilterRow key={f.id} filter={f} onApply={handleApply} onDelete={handleDelete} />
              ))}

              {shared.length > 0 && (
                <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                  Shared in my org
                </div>
              )}
              {shared.map((f) => (
                <FilterRow key={f.id} filter={f} onApply={handleApply} />
              ))}
            </>
          )}

          <div className="border-t border-gray-200 dark:border-acs-dark-border p-2">
            {showSaveForm ? (
              <div className="space-y-2">
                <input
                  type="text"
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Filter name"
                  className="w-full text-sm px-2 py-1.5 rounded-md border border-gray-300 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface text-gray-900 dark:text-acs-dark-text focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-acs-dark-text">
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input type="radio" checked={visibility === "PRIVATE"} onChange={() => setVisibility("PRIVATE")} />
                    <Lock size={12} /> Private
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input type="radio" checked={visibility === "ORG"} onChange={() => setVisibility("ORG")} />
                    <Users size={12} /> Shared with my org
                  </label>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setShowSaveForm(false)}
                    className="px-2.5 py-1 text-xs rounded-md border border-gray-300 dark:border-acs-dark-border text-gray-600 dark:text-acs-dark-text"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={busy || !name.trim()}
                    className="px-2.5 py-1 text-xs rounded-md bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                  >
                    {busy ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowSaveForm(true)}
                disabled={!hasActiveFilter}
                title={hasActiveFilter ? "Save the current filter" : "Build a filter first"}
                className="w-full inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-dashed border-gray-300 dark:border-acs-dark-border text-gray-600 dark:text-acs-dark-text hover:border-blue-400 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save size={13} /> Save current filter
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const FilterRow = ({ filter, onApply, onDelete }) => (
  <div
    onClick={() => onApply(filter)}
    className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-white/[0.03] cursor-pointer group"
  >
    <div className="flex items-center gap-1.5 min-w-0">
      {filter.visibility === "ORG" ? (
        <Users size={12} className="text-gray-400 flex-shrink-0" />
      ) : (
        <Lock size={12} className="text-gray-400 flex-shrink-0" />
      )}
      <span className="text-sm text-gray-800 dark:text-acs-dark-text truncate">{filter.name}</span>
    </div>
    {onDelete && (
      <button
        onClick={(e) => onDelete(filter.id, e)}
        className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity flex-shrink-0"
      >
        <Trash2 size={13} />
      </button>
    )}
  </div>
);

export default SavedFiltersControl;
